import { useState, type FormEvent, type ReactNode } from "react";
import type {
  AdminBusinessListingApplicationMessageSummaryResponse,
  AdminBusinessListingApplicationSummaryResponse,
  AdminBusinessListingListItemResponse,
} from "../../../types/adminTypes";
import { formatBusinessListingCompensation, formatBusinessListingDuration, formatBusinessListingJobType } from "../../../utils/BusinessListingUtils";
import { formatDate, formatDateTime } from "../../../utils/DateUtils";
import { formatCityStateZip } from "../../../utils/LocationUtils";
import Button from "../../ui/Button/Button";
import Modal from "../../ui/Modal/Modal";
import AdminPillBadge from "../ui/AdminPillBadge";
import AdminTableShell from "../ui/AdminTableShell";
import type { DataTableColumn } from "../../ui/DataTable/types";
import { adminApi } from "../../../lib/admin";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiErrorToMessage } from "../../../utils/ApiUtils";

type AdminBusinessListingsTableProps = {
  listings: AdminBusinessListingListItemResponse[];
  isLoading: boolean;
  page: number;
  pageSize: number;
  resultsCount: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  filters: ReactNode;
  onPageChange: (nextPage: number) => void;
};

const columns: DataTableColumn[] = [
  { key: "listing", label: "Listing" },
  { key: "business", label: "Business" },
  { key: "status", label: "Status" },
  { key: "location", label: "Location" },
  { key: "pay", label: "Pay" },
  { key: "created", label: "Created" },
  { key: "actions", label: "" },
];

const statusTone: Record<AdminBusinessListingListItemResponse["status"], "neutral" | "success" | "warning" | "danger"> = {
  DRAFT: "neutral",
  PUBLISHED: "success",
  EXPIRED: "warning",
  CLOSED: "danger",
};

const applicationStatusTone: Record<NonNullable<AdminBusinessListingApplicationSummaryResponse["status"]>, "success" | "warning" | "danger"> = {
  APPLIED: "success",
  WITHDRAWN: "warning",
  REJECTED: "danger",
};

type ModerationAction = "block" | "unblock";

const MAX_MODERATION_REASON_LENGTH = 500;

const getPosterName = (listing: AdminBusinessListingListItemResponse) => (
  [listing.posterFirstName, listing.posterLastName].filter(Boolean).join(" ") || listing.businessName || "Unknown business"
);

const getMessageSenderLabel = (message: AdminBusinessListingApplicationMessageSummaryResponse) => {
  const senderName = [message.senderFirstName, message.senderLastName].filter(Boolean).join(" ");
  const roleLabel = message.senderRole === "BUSINESS" ? "Business" : "Teen";
  return `${senderName || roleLabel} (${roleLabel})`;
};

const AdminBusinessListingDetailModal = ({
  listing,
  isOpen,
  onClose,
}: {
  listing: AdminBusinessListingListItemResponse | null;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const queryClient = useQueryClient();
  const [moderationAction, setModerationAction] = useState<ModerationAction | null>(null);
  const [moderationReason, setModerationReason] = useState("");
  const [moderationError, setModerationError] = useState<string | null>(null);

  const detailQuery = useQuery({
    queryKey: ["admin-business-listing-detail", listing?.id],
    queryFn: () => adminApi.getBusinessListingDetail(listing!.id),
    enabled: isOpen && listing !== null,
  });
  const detail = detailQuery.data;

  const blockListingMutation = useMutation({
    mutationFn: ({ listingId, reason }: { listingId: string; reason: string }) => (
      adminApi.blockBusinessListing(listingId, { reason })
    ),
    onSuccess: (updatedDetail) => {
      queryClient.setQueryData(["admin-business-listing-detail", updatedDetail.id], updatedDetail);
      void queryClient.invalidateQueries({ queryKey: ["admin-gigs"] });
      setModerationAction(null);
      setModerationReason("");
      setModerationError(null);
    },
  });

  const unblockListingMutation = useMutation({
    mutationFn: ({ listingId, reason }: { listingId: string; reason: string }) => (
      adminApi.unblockBusinessListing(listingId, { reason })
    ),
    onSuccess: (updatedDetail) => {
      queryClient.setQueryData(["admin-business-listing-detail", updatedDetail.id], updatedDetail);
      void queryClient.invalidateQueries({ queryKey: ["admin-gigs"] });
      setModerationAction(null);
      setModerationReason("");
      setModerationError(null);
    },
  });

  const moderationPending = blockListingMutation.isPending || unblockListingMutation.isPending;

  const closeModerationModal = () => {
    if (moderationPending) {
      return;
    }
    setModerationAction(null);
    setModerationReason("");
    setModerationError(null);
    blockListingMutation.reset();
    unblockListingMutation.reset();
  };

  const handleClose = () => {
    if (moderationPending) {
      return;
    }
    closeModerationModal();
    onClose();
  };

  const handleModerationSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!detail || moderationAction === null) {
      return;
    }
    const reason = moderationReason.trim();
    if (!reason) {
      setModerationError(
        `Enter a reason before ${moderationAction === "block" ? "blocking" : "unblocking"} this business listing.`,
      );
      return;
    }
    setModerationError(null);
    if (moderationAction === "block") {
      blockListingMutation.mutate({ listingId: detail.id, reason });
      return;
    }
    unblockListingMutation.mutate({ listingId: detail.id, reason });
  };

  const moderationMutationError = blockListingMutation.error
    ? apiErrorToMessage(blockListingMutation.error, "Failed to block this business listing.")
    : unblockListingMutation.error
      ? apiErrorToMessage(unblockListingMutation.error, "Failed to unblock this business listing.")
      : null;

  const isUnblockAction = moderationAction === "unblock";
  const moderationActionLabel = isUnblockAction ? "Unblock listing" : "Block listing";

  const openModerationModal = (action: ModerationAction) => {
    setModerationAction(action);
    setModerationReason("");
    setModerationError(null);
    blockListingMutation.reset();
    unblockListingMutation.reset();
  };

  const moderationSubmitLabel = moderationPending
    ? `${isUnblockAction ? "Unblocking" : "Blocking"}...`
    : moderationActionLabel;

  const moderationDescription = isUnblockAction
    ? "Unblocking this listing makes it eligible for public business listing discovery again when its status allows it."
    : "Blocking this listing removes it from public business listing discovery and stops new applications.";

  const moderationPlaceholder = isUnblockAction
    ? "Add the reason this listing is being unblocked"
    : "Add the reason this listing is being blocked";

  const moderationColorClasses = isUnblockAction
    ? "!border-emerald-200 !bg-emerald-600 !text-white hover:!bg-emerald-700 hover:!text-white"
    : "!border-rose-200 !bg-rose-600 !text-white hover:!bg-rose-700 hover:!text-white";

  const moderationNoticeClasses = isUnblockAction
    ? "rounded-2xl border border-emerald-200 bg-emerald-50 p-4"
    : "rounded-2xl border border-rose-200 bg-rose-50 p-4";

  const moderationNoticeTextClasses = isUnblockAction
    ? "text-sm text-emerald-800"
    : "text-sm text-rose-800";

  const moderationReasonInputId = isUnblockAction
    ? "admin-unblock-business-listing-reason"
    : "admin-block-business-listing-reason";

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} title={listing?.title ?? "Business listing"} panelClassName="max-w-4xl">
        {detailQuery.isLoading ? <p className="text-sm text-myteengig-text-muted">Loading business listing detail...</p> : null}
        {detailQuery.error ? <p className="text-sm text-red-600">{apiErrorToMessage(detailQuery.error, "Failed to load business listing detail.")}</p> : null}
        {detail ? (
          <div className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-2xl border border-myteengig-border p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Status</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <AdminPillBadge tone={statusTone[detail.status]}>{detail.status}</AdminPillBadge>
                  {detail.blocked ? <AdminPillBadge tone="danger">Blocked</AdminPillBadge> : null}
                </div>
              </div>
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Payment</p>
              <p className="mt-3 text-sm text-myteengig-text-primary">{detail.publicationPaymentStatus}</p>
            </div>
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Applications</p>
              <p className="mt-3 text-sm text-myteengig-text-primary">{detail.applicationCount}</p>
            </div>
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Duration</p>
              <p className="mt-3 text-sm text-myteengig-text-primary">{formatBusinessListingDuration(detail.estimatedDurationMinutes) ?? "Not provided"}</p>
            </div>
            </div>

            <div className={`rounded-2xl border p-4 ${detail.blocked ? "border-rose-200 bg-rose-50" : "border-myteengig-border"}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-myteengig-text-primary">Block status</p>
                  <p className="mt-2 text-sm text-myteengig-text-muted">
                    {detail.blocked
                      ? `Blocked${detail.blockedAt ? ` ${formatDateTime(detail.blockedAt)}` : ""}`
                      : "No block applied."}
                  </p>
                </div>
                {detail.blocked ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <AdminPillBadge tone="danger">Blocked</AdminPillBadge>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                      onClick={() => openModerationModal("unblock")}
                    >
                      Unblock listing
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="border-rose-200 text-rose-700 hover:bg-rose-50"
                    onClick={() => openModerationModal("block")}
                  >
                    Block listing
                  </Button>
                )}
              </div>
              {detail.blocked ? (
                <p className="mt-3 whitespace-pre-wrap text-sm text-rose-800">
                  {detail.blockedReason ?? "No reason recorded."}
                </p>
              ) : null}
            </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">Listing details</p>
              <div className="mt-3 space-y-2 text-sm text-myteengig-text-muted">
                <p>{detail.category}</p>
                <p>{detail.jobType ? formatBusinessListingJobType(detail.jobType) : "Job type not set"}</p>
                <p>{formatCityStateZip(detail)}</p>
                <p>
                  {detail.payType && detail.minPayAmountCents !== null && detail.maxPayAmountCents !== null
                    ? formatBusinessListingCompensation(detail.payType, detail.minPayAmountCents, detail.maxPayAmountCents, detail.jobType)
                    : "Pay not set"}
                </p>
                <p>Published: {detail.publishedAt ? formatDateTime(detail.publishedAt) : "Not published"}</p>
                <p>Expires: {detail.expiresAt ? formatDateTime(detail.expiresAt) : "Not set"}</p>
              </div>
            </div>
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">Business</p>
              <div className="mt-3 space-y-2 text-sm text-myteengig-text-muted">
                <p>{detail.businessName ?? getPosterName(detail)}</p>
                <p>{detail.posterEmail ?? "No email"}</p>
                <p>{detail.businessWebsite ?? "No website"}</p>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-myteengig-border p-4">
            <p className="text-sm font-semibold text-myteengig-text-primary">Description</p>
            <p className="mt-3 whitespace-pre-wrap text-sm text-myteengig-text-muted">{detail.description}</p>
          </div>

          <div className="rounded-2xl border border-myteengig-border p-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-semibold text-myteengig-text-primary">Applicants</p>
              <AdminPillBadge>{detail.applications.length}</AdminPillBadge>
            </div>
            {detail.applications.length > 0 ? (
              <div className="mt-4 grid gap-3">
                {detail.applications.map((application) => (
                  <div
                    key={application.applicationId}
                    className="rounded-lg border border-myteengig-border bg-myteengig-background/50 px-4 py-3"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-myteengig-text-primary">
                          {application.teenDisplayName}
                        </p>
                        <p className="mt-1 truncate text-sm text-myteengig-text-muted">
                          {application.teenEmail ?? "No email provided"}
                        </p>
                        <p className="mt-2 text-xs text-myteengig-text-muted">
                          Applied: {formatDateTime(application.createdAt)}
                        </p>
                      </div>
                      {application.status ? (
                        <AdminPillBadge tone={applicationStatusTone[application.status]}>{application.status}</AdminPillBadge>
                      ) : null}
                    </div>

                    <div className="mt-3 grid gap-3 md:grid-cols-2">
                      <div className="rounded-lg border border-myteengig-border bg-white px-4 py-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Teen profile</p>
                        <div className="mt-2 space-y-2 text-sm text-myteengig-text-muted">
                          <p>Age: {application.teenAge ?? "Not provided"}</p>
                          <p>{application.teenBio ?? "No bio provided."}</p>
                          <p>
                            Skills: {application.teenSkills.length > 0 ? application.teenSkills.join(", ") : "No skills listed"}
                          </p>
                        </div>
                      </div>
                      <div className="rounded-lg border border-myteengig-border bg-white px-4 py-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Resume</p>
                        {application.resume ? (
                          <a
                            href={application.resume.url}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-2 inline-flex text-sm font-semibold text-myteengig-primary hover:underline"
                          >
                            {application.resume.originalFileName ?? "Open resume"}
                          </a>
                        ) : (
                          <p className="mt-2 text-sm text-myteengig-text-muted">No resume attached.</p>
                        )}
                      </div>
                    </div>

                    <div className="mt-3 rounded-lg border border-myteengig-border bg-white px-4 py-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Application note</p>
                      <p className="mt-2 whitespace-pre-wrap text-sm text-myteengig-text-primary">
                        {application.message?.trim() || "No application note included."}
                      </p>
                      {application.rejectionReason ? (
                        <p className="mt-3 whitespace-pre-wrap text-sm text-rose-700">
                          Rejection reason: {application.rejectionReason}
                        </p>
                      ) : null}
                    </div>

                    <div className="mt-3 rounded-lg border border-myteengig-border bg-white px-4 py-3">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Messages</p>
                        <AdminPillBadge>{application.messages.length}</AdminPillBadge>
                      </div>
                      {application.messages.length > 0 ? (
                        <div className="mt-3 space-y-3">
                          {application.messages.map((message) => (
                            <div key={message.id} className="rounded-lg border border-myteengig-border bg-myteengig-background/40 px-4 py-3">
                              <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-myteengig-text-primary">
                                    {getMessageSenderLabel(message)}
                                  </p>
                                  <p className="mt-1 text-xs text-myteengig-text-muted">
                                    {message.senderEmail ?? "No email"} · {formatDateTime(message.createdAt)}
                                  </p>
                                </div>
                              </div>
                              <p className="mt-3 whitespace-pre-wrap break-words text-sm text-myteengig-text-primary">{message.body}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="mt-3 text-sm text-myteengig-text-muted">No message thread for this applicant.</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-3 text-sm text-myteengig-text-muted">No applicants for this business listing yet.</p>
            )}
          </div>

          <div className="rounded-2xl border border-myteengig-border p-4">
            <p className="text-sm font-semibold text-myteengig-text-primary">Stripe references</p>
            <div className="mt-3 space-y-2 break-all text-sm text-myteengig-text-muted">
              <p>Checkout session: {detail.stripeCheckoutSessionId ?? "Not available"}</p>
              <p>Payment intent: {detail.stripePaymentIntentId ?? "Not available"}</p>
            </div>
          </div>
          </div>
        ) : null}
      </Modal>

      <Modal isOpen={isOpen && moderationAction !== null} onClose={closeModerationModal} title={moderationActionLabel} panelClassName="max-w-xl">
        <form className="space-y-5" onSubmit={handleModerationSubmit}>
          <div className={moderationNoticeClasses}>
            <p className={moderationNoticeTextClasses}>{moderationDescription}</p>
          </div>

          <div>
            <label htmlFor={moderationReasonInputId} className="mb-2 block text-sm font-semibold text-myteengig-text-primary">
              Reason
            </label>
            <textarea
              id={moderationReasonInputId}
              value={moderationReason}
              onChange={(event) => {
                setModerationReason(event.target.value);
                setModerationError(null);
              }}
              maxLength={MAX_MODERATION_REASON_LENGTH}
              rows={5}
              className="w-full rounded-lg border border-myteengig-border px-3 py-2 text-sm text-myteengig-text-primary shadow-xs focus:border-myteengig-primary focus:outline-none focus:ring-2 focus:ring-myteengig-primary/20"
              placeholder={moderationPlaceholder}
              required
            />
            <p className="mt-2 text-xs text-myteengig-text-muted">
              {moderationReason.length}/{MAX_MODERATION_REASON_LENGTH}
            </p>
          </div>

          {moderationError || moderationMutationError ? (
            <p className="text-sm font-medium text-red-600">{moderationError ?? moderationMutationError}</p>
          ) : null}

          <div className="flex flex-wrap justify-end gap-3">
            <Button type="button" variant="outline" onClick={closeModerationModal} disabled={moderationPending}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="outline"
              className={moderationColorClasses}
              disabled={moderationPending}
            >
              {moderationSubmitLabel}
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
};

const AdminBusinessListingsTable = ({
  listings,
  isLoading,
  page,
  pageSize,
  resultsCount,
  hasNextPage,
  hasPreviousPage,
  filters,
  onPageChange,
}: AdminBusinessListingsTableProps) => {
  const [selectedListing, setSelectedListing] = useState<AdminBusinessListingListItemResponse | null>(null);

  const renderListingSummary = (listing: AdminBusinessListingListItemResponse) => (
    <div className="space-y-1">
      <p className="font-semibold">{listing.title}</p>
      <p className="text-myteengig-text-muted">{listing.category}</p>
      <p className="text-xs text-myteengig-text-muted">
        {listing.jobType ? formatBusinessListingJobType(listing.jobType) : "Job type not set"}
      </p>
    </div>
  );

  return (
    <>
      <AdminTableShell
        columns={columns}
        filters={filters}
        mobileCards={listings.map((listing) => (
          <article key={listing.id} className="rounded-[1.5rem] border border-myteengig-border bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">{renderListingSummary(listing)}</div>
              <div className="flex flex-col items-end gap-2">
                <AdminPillBadge tone={statusTone[listing.status]}>{listing.status}</AdminPillBadge>
                {listing.blocked ? <AdminPillBadge tone="danger">Blocked</AdminPillBadge> : null}
              </div>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Business</p>
                <p className="mt-1 text-sm font-medium text-myteengig-text-primary">{getPosterName(listing)}</p>
                <p className="mt-1 text-sm text-myteengig-text-muted">{listing.posterEmail ?? "No email"}</p>
              </div>
              <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Pay</p>
                <p className="mt-1 text-sm text-myteengig-text-primary">
                  {listing.payType && listing.minPayAmountCents !== null && listing.maxPayAmountCents !== null
                    ? formatBusinessListingCompensation(listing.payType, listing.minPayAmountCents, listing.maxPayAmountCents, listing.jobType)
                    : "Pay not set"}
                </p>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <Button variant="outline" size="sm" className="w-full sm:w-auto" onClick={() => setSelectedListing(listing)}>
                View
              </Button>
            </div>
          </article>
        ))}
        rows={listings.map((listing) => (
          <tr key={listing.id} className="align-top">
            <td className="px-4 py-4 text-sm text-myteengig-text-primary">{renderListingSummary(listing)}</td>
            <td className="px-4 py-4 text-sm text-myteengig-text-primary">
              <div className="space-y-1">
                <p className="font-semibold">{getPosterName(listing)}</p>
                <p className="text-myteengig-text-muted">{listing.posterEmail ?? "No email"}</p>
              </div>
            </td>
            <td className="px-4 py-4 text-sm text-myteengig-text-primary">
              <div className="space-y-2">
                <AdminPillBadge tone={statusTone[listing.status]}>{listing.status}</AdminPillBadge>
                {listing.blocked ? <AdminPillBadge tone="danger">Blocked</AdminPillBadge> : null}
                <p className="text-xs text-myteengig-text-muted">{listing.publicationPaymentStatus}</p>
              </div>
            </td>
            <td className="px-4 py-4 text-sm text-myteengig-text-primary">{formatCityStateZip(listing)}</td>
            <td className="px-4 py-4 text-sm text-myteengig-text-primary">
              {listing.payType && listing.minPayAmountCents !== null && listing.maxPayAmountCents !== null
                ? formatBusinessListingCompensation(listing.payType, listing.minPayAmountCents, listing.maxPayAmountCents, listing.jobType)
                : "Pay not set"}
            </td>
            <td className="px-4 py-4 text-sm text-myteengig-text-primary">{formatDate(listing.createdAt)}</td>
            <td className="px-4 py-4 text-sm text-myteengig-text-primary">
              <Button variant="outline" size="sm" onClick={() => setSelectedListing(listing)}>
                View
              </Button>
            </td>
          </tr>
        ))}
        isLoading={isLoading}
        isEmpty={!isLoading && listings.length === 0}
        emptyMessage="No business listings found for the current filters."
        page={page}
        pageSize={pageSize}
        resultsCount={resultsCount}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        onPageChange={onPageChange}
      />
      <AdminBusinessListingDetailModal listing={selectedListing} isOpen={selectedListing !== null} onClose={() => setSelectedListing(null)} />
    </>
  );
};

export default AdminBusinessListingsTable;
